import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { canCommentVisitReport, canWriteVisitReport, visitPhotos } from "@/lib/planner/visitReport";
import { fetchVisitReport, visitReportQueryKey } from "@/lib/planner/visitReportApi";
import { VisitReportMeta } from "@/components/perito/VisitReportMeta";
import { VisitCommentDialog } from "@/components/perito/VisitCommentDialog";
import { useTranslation } from "react-i18next";
import { PersonAvatar } from "@/components/ui/PersonAvatar";
import { formatDateTime, formatDate } from "@/lib/dateFormatters";
import { deleteTaskWithCascade, getLinkedSprayLogsForTask, taskDeleteErrorKey, type LinkedSprayLog } from "@/lib/taskDeletion";
import { describeAppliedProducts, getAppliedSummaryForTask, type AppliedSummary } from "@/lib/appliedSummary";
import { supabase } from "@/integrations/supabase/client";
import { getTargetBlocks } from "@/hooks/useBlockNames";
import { toast } from "sonner";
import { Drawer, DrawerClose, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import { getTaskTypeIcon, taskTypeTileShadow } from "@/lib/taskTypeIcon";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Calendar,
  MapPin,
  Droplets,
  Bug,
  FlaskConical,
  User,
  Clock,
  CheckCircle2,
  XCircle,
  Timer,
  Package,
  ClipboardList,
  Pencil,
  MessageSquareText,
  Trash2,
  Link2,
  Rows,
  Play,
  Square,
  RotateCcw,
  Flag,
  ChevronDown,
  House,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { SheetCloseIcon, SHEET_CLOSE_CLASS } from "@/components/ui/sheetChrome";
import { SheetBodySkeleton } from "@/components/ui/SheetBodySkeleton";
import { useSheetSettled } from "@/hooks/use-sheet-settled";

/* LOS COLORES DE LAS BALDOSAS DE LA FICHA. Los mismos que la ficha de un parte de
   campo (`ScoutingSessionDetailDrawer`), que es de donde viene este diseño. */
const TILE_BLUE = "#2B7DE0";
const TILE_GREEN = "#059669";
const TILE_AMBER = "#D97706";
const TILE_VIOLET = "#8B5CF6";
const TILE_SKY = "#0EA5E9";
const TILE_SLATE = "#94A3B8";

/**
 * UNA FILA DE LA FICHA: baldosa de color, rótulo pequeño en versalitas y el dato
 * debajo, todas separadas por un filete.
 *
 * POR QUÉ (usuario 05-09-2026). La ficha de una tarea contaba lo mismo de cinco
 * maneras distintas: la fecha suelta bajo el título, el sitio en una tira de
 * cosas en línea, la persona como una foto sin rótulo perdida entre medias, y
 * los productos en otra caja con otro borde y otro relleno. La ficha de un parte
 * de campo ya tenía resuelto el problema —una tabla de filas iguales, cada una
 * con su baldosa y su rótulo— y el usuario pidió esa misma. Así que es la misma:
 * fecha, sitio, quién lo hace, la plaga si la hay y los productos, en ese orden.
 */
function FilaFicha({
  tile,
  icon: Icon,
  avatar,
  label,
  children,
}: {
  tile?: string;
  icon?: React.ComponentType<{ className?: string }>;
  /** Para «Asignado a», donde la baldosa es la foto de la persona. */
  avatar?: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3 p-3">
      {avatar ?? (
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
          style={{ backgroundColor: tile, boxShadow: taskTypeTileShadow(tile ?? TILE_SLATE) }}
        >
          {Icon && <Icon className="w-4 h-4 text-white" />}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
        <div className="font-semibold text-sm text-slate-900">{children}</div>
      </div>
    </div>
  );
}
import { PILL_BASE, PILL_COLORS, type PillColor } from "@/lib/statusPill";
import { usePestTranslation } from "@/hooks/usePestTranslation";
import { VisitCompletionDialog } from "@/components/perito/VisitCompletionDialog";
import { ScoutingSessionDetailDrawer } from "@/components/pests/ScoutingSessionDetailDrawer";
import { translateTaskTitle } from "@/lib/taskTitle";
import { ImgOffline } from "@/components/offline/ImgOffline";

/** Lo mínimo que pide ScoutingSessionDetailDrawer; la ficha se trae al pulsar. */
interface LinkedScoutingSession {
  id: string;
  date: string;
  greenhouse_id: string | null;
  block_id: string | null;
  notes: string | null;
  created_at?: string;
  inspector_id?: string | null;
  greenhouse?: { name: string };
  block?: { name: string };
}

interface Task {
  id: string;
  title: string;
  type: string;
  status: string;
  priority: string;
  scheduled_date: string;
  greenhouse_id: string | null;
  block_id: string | null;
  treatment_id: string | null;
  pest_id: string | null;
  amount_planned: number | null;
  amount_used: number | null;
  notes: string | null;
  custom_products?: Array<{
    product_name: string;
    amount: number;
    unit: string;
    rate_value?: number;
    rate_unit?: string;
    dose_value?: number;
    dose_unit?: string;
    water_volume?: number;
    water_volume_unit?: string;
    target_blocks?: string[];
    observed_rows?: Record<string, { row_start: number | null; row_end: number | null }>;
  }>;
  assigned_to: string | null;
  scouting_session_id?: string | null;
  created_at?: string | null;
  completed_at?: string | null;
  actual_start_time?: string | null;
  actual_end_time?: string | null;
  time_spent?: number | null;
  visit_report?: any;
  photos?: string[] | null;
  greenhouses?: { name: string };
  blocks?: { name: string };
  treatments?: { name: string };
  pests?: { name: string; translation_key?: string | null };
  profiles?: { full_name: string; avatar_url?: string | null };
}

interface TaskDetailDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  task: Task | null;
  onEdit?: (task: Task) => void;
  onDelete?: (task: Task) => void;
  onRefresh?: () => void;
  canEdit?: boolean;
  canDelete?: boolean;
  // New optional handlers — when provided, sticky action bar is shown
  onComplete?: (task: Task) => void | Promise<void>;
  onStatusChange?: (taskId: string, newStatus: string) => void | Promise<void>;
  onUndo?: (taskId: string) => void | Promise<void>;
  onRequestDelete?: (taskId: string) => void;
  locationName?: string | null;
  isLoading?: boolean;
}

// One accent colour drives the whole popup, picked from the task status.
// Used softly (tinted backgrounds, light borders) — never as a heavy solid band.
const ACCENT: Record<string, { tile: string; pill: string; dot: string }> = {
  pending: {
    tile: "bg-amber-400/12 border-amber-400/40 text-amber-600",
    pill: "bg-amber-400/15 text-amber-700 border-amber-400/30 hover:bg-amber-400/15",
    dot: "bg-amber-500",
  },
  in_progress: {
    tile: "bg-blue-500/12 border-blue-500/40 text-blue-600",
    pill: "bg-blue-500/15 text-blue-700 border-blue-500/30 hover:bg-blue-500/15",
    dot: "bg-blue-500",
  },
  completed: {
    tile: "bg-teal-500/12 border-teal-500/40 text-teal-600",
    pill: "bg-teal-500/15 text-teal-700 border-teal-500/30 hover:bg-teal-500/15",
    dot: "bg-teal-500",
  },
  cancelled: {
    tile: "bg-slate-400/12 border-slate-300 text-slate-500",
    pill: "bg-slate-100 text-slate-500 border-slate-200 hover:bg-slate-100",
    dot: "bg-slate-400",
  },
};

// Solid-pill colour per task status (unified status-pill style).
const STATUS_PILL_COLOR: Record<string, PillColor> = {
  pending: "amber",
  in_progress: "blue",
  completed: "green",
  cancelled: "red",
};

// Solid-pill colour per task type (unified status-pill style).
const TYPE_PILL_COLOR: Record<string, PillColor> = {
  treatment: "indigo",
  monitoring: "blue",
  scouting: "blue",
  maintenance: "amber",
  visit: "teal",
  inspection: "green",
  other: "slate",
};

export function TaskDetailDrawer({
  open,
  onOpenChange,
  task,
  onEdit,
  onDelete,
  onRefresh,
  canEdit = false,
  canDelete = false,
  onComplete,
  onStatusChange,
  onUndo,
  onRequestDelete,
  locationName,
  isLoading,
}: TaskDetailDrawerProps) {
  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation();
  const { getName: getPestName } = usePestTranslation();
  // Falso mientras la hoja sube (el porqué, junto al cuerpo).
  const settled = useSheetSettled(open);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  // «Marcar pendiente» pregunta antes, con lo que se deshace (A17 · T12-02).
  const [undoDialogOpen, setUndoDialogOpen] = useState(false);
  const [undoSummary, setUndoSummary] = useState<AppliedSummary | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [linkedSprayLogs, setLinkedSprayLogs] = useState<LinkedSprayLog[]>([]);
  const [targetBlockNames, setTargetBlockNames] = useState<string[]>([]);
  const [blockNameMap, setBlockNameMap] = useState<Map<string, string>>(new Map());
  const [blockAreaMap, setBlockAreaMap] = useState<Map<string, number | null>>(new Map());
  // Componentes del tratamiento del catálogo (ver el efecto más abajo).
  const [treatmentComponents, setTreatmentComponents] = useState<
    Array<{ id: string; product_name: string; rate_value: number | null; rate_unit: string | null }>
  >([]);
  const [editReportOpen, setEditReportOpen] = useState(false);
  /*
   * Muestreo vinculado. La insignia «Vinculada al muestreo» solo lo decía; ahora abre la
   * ficha del muestreo ENCIMA de esta, sin salir de la página, y al cerrarla se vuelve a
   * ver la tarea. La sesión se pide al pulsar y no al abrir la tarea: la mayoría no
   * tienen muestreo, y las que lo tienen casi nunca se consulta.
   */
  const [scoutingOpen, setScoutingOpen] = useState(false);
  const [scoutingSession, setScoutingSession] = useState<LinkedScoutingSession | null>(null);
  const [scoutingLoading, setScoutingLoading] = useState(false);
  // Per-product expansion for the per-sector breakdown (collapsed by default to keep the drawer compact).
  const [expandedProducts, setExpandedProducts] = useState<Set<number>>(new Set());
  const toggleProduct = (idx: number) =>
    setExpandedProducts(prev => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });

  /** Trae el muestreo vinculado y lo abre encima. Si falla, se avisa y no se abre nada. */
  const openLinkedScouting = async () => {
    const sessionId = task?.scouting_session_id;
    if (!sessionId || scoutingLoading) return;
    // Ya cargado: se reabre sin volver a pedirlo.
    if (scoutingSession?.id === sessionId) {
      setScoutingOpen(true);
      return;
    }
    setScoutingLoading(true);
    try {
      const { data, error } = await supabase
        .from("pest_scouting_sessions")
        .select(
          "id, date, created_at, greenhouse_id, block_id, notes, inspector_id, " +
            "greenhouse:greenhouses(name), block:blocks(name)",
        )
        .eq("id", sessionId)
        .maybeSingle();
      if (error) throw error;
      if (!data) {
        toast.error(t("planner.scoutingNotFound"));
        return;
      }
      setScoutingSession(data as unknown as LinkedScoutingSession);
      setScoutingOpen(true);
    } catch (err) {
      console.error("[TaskDetailDrawer] No se pudo abrir el muestreo vinculado:", err);
      toast.error(t("planner.scoutingOpenError"));
    } finally {
      setScoutingLoading(false);
    }
  };

  const isVisitOrInspection = task?.type === "visit" || task?.type === "inspection";
  /* EL INFORME SE LEE AQUÍ, de la base (C3 / T07-01). La ficha se abre desde el
     planificador, Tareas, Equipo o un aviso, y cada uno pide columnas distintas:
     el planificador del admin no pedía `visit_report` ni `photos`, así que el
     admin veía «Sin informe todavía» en una visita con informe y fotos. */
  const { user: authUser, role: authRole } = useAuth();
  const visitQuery = useQuery({
    queryKey: visitReportQueryKey(task?.id),
    queryFn: () => fetchVisitReport(task!.id),
    enabled: open && !!task?.id && isVisitOrInspection,
  });
  const visitRow = visitQuery.data;
  const visitReport = (visitRow?.visit_report ?? task?.visit_report) as any;
  const reportPhotos = visitRow ? visitPhotos(visitRow) : visitPhotos(task);
  /* El informe es del perito que tiene la visita (A5): «Editar informe» solo a él
     (o a quien pueda editar si nadie la tiene asignada). La empresa deja su
     comentario aparte y nunca escribe encima. */
  const canWriteReport = canEdit && !!visitRow && canWriteVisitReport(visitRow, authUser?.id);
  const canCommentReport = canEdit && !!visitRow && !canWriteReport && canCommentVisitReport(authRole);
  const [commentOpen, setCommentOpen] = useState(false);

  // Resolve target block names and areas when task changes — collect ALL block IDs
  // referenced by the task (task-level + every product's target_blocks) so per-product
  // breakdowns always have a real name and never fall back to a truncated UUID.
  useEffect(() => {
    if (!task) {
      setTargetBlockNames([]);
      setBlockNameMap(new Map());
      setBlockAreaMap(new Map());
      return;
    }
    const idSet = new Set<string>();
    if (task.block_id) idSet.add(task.block_id);
    for (const id of getTargetBlocks(task)) idSet.add(id);
    if (Array.isArray(task.custom_products)) {
      for (const p of task.custom_products) {
        if (Array.isArray(p?.target_blocks)) {
          for (const id of p.target_blocks) {
            if (typeof id === "string") idSet.add(id);
          }
        }
      }
    }
    const allBlockIds = Array.from(idSet);
    if (allBlockIds.length === 0) {
      setTargetBlockNames([]);
      setBlockNameMap(new Map());
      setBlockAreaMap(new Map());
      return;
    }
    const fetchNames = async () => {
      const { data } = await supabase
        .from("blocks")
        .select("id, name, area_m2")
        .in("id", allBlockIds);
      if (data) {
        const nameMap = new Map(data.map(b => [b.id, b.name]));
        setBlockNameMap(nameMap);
        const orderedIds = getTargetBlocks(task);
        setTargetBlockNames(orderedIds.map(id => nameMap.get(id)).filter(Boolean) as string[]);
        const areaMap = new Map<string, number | null>();
        data.forEach(b => areaMap.set(b.id, b.area_m2));
        setBlockAreaMap(areaMap);
      }
    };
    fetchNames();
  }, [task]);

  // Una tarea que apunta a un tratamiento del catálogo NO guarda custom_products:
  // sus productos viven en treatment_components. La ficha solo miraba
  // custom_products, así que decía "Sin productos registrados" de tareas que sí
  // los tienen — y encima el resumen de abajo enseñaba el nombre del tratamiento.
  useEffect(() => {
    const treatmentId = task?.treatment_id;
    const hasCustom = Array.isArray(task?.custom_products) && task.custom_products.length > 0;
    if (!treatmentId || hasCustom) {
      setTreatmentComponents([]);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("treatment_components")
        .select("id, rate_value, rate_unit, inventory:product_id(product_name)")
        .eq("treatment_id", treatmentId);
      if (cancelled || !data) return;
      setTreatmentComponents(
        data
          .map((c) => ({
            id: c.id as string,
            product_name: (c.inventory as { product_name: string } | null)?.product_name ?? "",
            rate_value: c.rate_value as number | null,
            rate_unit: c.rate_unit as string | null,
          }))
          .filter((c) => c.product_name)
      );
    })();
    return () => { cancelled = true; };
  }, [task?.treatment_id, task?.custom_products]);

  // Drawer stays open through status changes — task prop refreshes from parent
  // and badges/action buttons update automatically. Drawer only closes via X,
  // Edit (opens form), or after Delete confirmation.

  if (!task) return null;

  const handleEdit = () => {
    if (onEdit && task) {
      onEdit(task);
      onOpenChange(false);
    }
  };

  const handleDeleteClick = async () => {
    setLinkedSprayLogs([]);
    setDeleteDialogOpen(true);
    if (task) {
      const linked = await getLinkedSprayLogsForTask(task.id);
      setLinkedSprayLogs(linked);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!task) return;

    setIsDeleting(true);
    try {
      await deleteTaskWithCascade(task.id, queryClient);

      toast.success(t("planner.taskDeleted"));
      setDeleteDialogOpen(false);
      setLinkedSprayLogs([]);
      onOpenChange(false);
      if (onDelete) {
        onDelete(task);
      }
    } catch (error: any) {
      console.error("Error deleting task:", error);
      toast.error(t(taskDeleteErrorKey(error)));
    } finally {
      setIsDeleting(false);
    }
  };

  const typeStyle = getTaskTypeIcon(task.type);
  const TypeIcon = typeStyle.Icon;

  const hasActionBar = !!(onComplete || onStatusChange || onUndo || (canEdit && onEdit) || (canDelete && (onRequestDelete || onDelete)));
  const handleStart = () => onStatusChange?.(task.id, "in_progress");
  const handleStop = () => onStatusChange?.(task.id, "pending");
  const handleCompleteClick = async () => {
    await onComplete?.(task);
    // Drawer stays open until status actually flips (see effect below).
  };
  /* «Marcar pendiente» NUNCA de un toque (A17 · T12-02, 26-09-2026): en un
     tratamiento hecho quita la aplicación del cuaderno y devuelve el stock, así
     que antes se dice qué se deshace, con fecha, cantidades y finca. */
  const handleUndoClick = async () => {
    setUndoSummary(null);
    setUndoDialogOpen(true);
    if (task.type === "treatment") setUndoSummary(await getAppliedSummaryForTask(task.id));
  };
  const handleUndoConfirm = async () => {
    setUndoDialogOpen(false);
    await onUndo?.(task.id);
    // Drawer stays open until status actually flips (see effect below).
  };
  const undoText = task.type !== "treatment"
    ? t("planner.undoTaskWarning")
    : undoSummary && undoSummary.products.length > 0
      ? t(undoSummary.locationName ? "planner.undoAppliedWarning" : "planner.undoAppliedWarningNoLocation", {
          date: undoSummary.date ? formatDate(new Date(undoSummary.date + "T00:00:00"), "P") : "",
          products: describeAppliedProducts(undoSummary, (amount, product) => t("planner.amountOfProduct", { amount, product })),
          location: undoSummary.locationName ?? "",
        })
      : t("planner.undoAppliedWarningGeneric");
  const handleDeleteFromBar = () => {
    if (onRequestDelete) {
      onRequestDelete(task.id);
      onOpenChange(false);
    } else {
      handleDeleteClick();
    }
  };

  const treatmentMode = (task.treatments as any)?.mode as string | null | undefined;
  const isBio = treatmentMode === "biological";
  const isChem = treatmentMode === "chemical";

  const a = ACCENT[task.status] ?? ACCENT.pending;
  const hasProducts = Array.isArray(task.custom_products) && task.custom_products.length > 0;

  const sectionLabel = "px-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400";
  const groupedCard =
    "bg-white rounded-[14px] border border-slate-200/80 px-4 shadow-[0_1px_2px_rgba(15,39,68,0.04)] divide-y divide-slate-100";

  // Footer secondary controls. Start/Stop only makes sense while the task is
  // active — a completed task must first be re-opened via "Marcar pendiente"
  // (the primary action), so Comenzar is hidden until it's pending again.
  const ghostBtnBase =
    "h-11 rounded-xl text-[13px] font-semibold bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 shadow-none px-2 min-w-0 flex-1";
  const startStopBtn = onStatusChange && (task.status === "pending" || task.status === "in_progress")
    ? task.status === "in_progress"
      ? (
        <Button key="stop" onClick={handleStop} disabled={isLoading} className={ghostBtnBase}>
          <Square className="w-4 h-4 mr-1.5 shrink-0" fill="currentColor" />
          <span className="truncate">{t("planner.stopTask")}</span>
        </Button>
      ) : (
        <Button key="start" onClick={handleStart} disabled={isLoading} className={ghostBtnBase}>
          <Play className="w-4 h-4 mr-1.5 shrink-0" fill="currentColor" />
          <span className="truncate">{t("common.start")}</span>
        </Button>
      )
    : null;
  const editBtn = (canEdit && onEdit) ? (
    <Button key="edit" onClick={handleEdit} disabled={isLoading} className={ghostBtnBase}>
      <Pencil className="w-4 h-4 mr-1.5 shrink-0" />
      <span className="truncate">{t("common.edit")}</span>
    </Button>
  ) : null;
  // Compact solid-red trash button, matching the Plagas page delete style.
  const deleteBtn = (canDelete && (onRequestDelete || onDelete)) ? (
    <Button
      key="delete"
      onClick={handleDeleteFromBar}
      disabled={isLoading}
      aria-label={t("common.delete")}
      className="h-11 w-11 shrink-0 rounded-xl p-0 bg-destructive hover:bg-destructive/90 text-white shadow-none"
    >
      <Trash2 className="w-[18px] h-[18px]" />
    </Button>
  ) : null;
  const showComplete = !!onComplete && task.status !== "completed" && task.status !== "cancelled";
  const showMarkPending = task.status === "completed" && !!onUndo;
  // Secundario, no el botón grande: deshacer no es lo que se viene a hacer aquí (A17).
  const markPendingBtn = showMarkPending ? (
    <Button key="undo" onClick={handleUndoClick} disabled={isLoading} className={ghostBtnBase}>
      <RotateCcw className="w-4 h-4 mr-1.5 shrink-0" />
      <span className="truncate">{t("planner.markPending", "Marcar pendiente")}</span>
    </Button>
  ) : null;
  const hasSecondaryRow = !!(markPendingBtn || startStopBtn || editBtn || deleteBtn);

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
        {/* Esta ficha se pintaba con Radix a mano: su propio portal, su propio velo,
            su propio tirador y su propia copia de las clases de colocación. Era la
            misma hoja que `ui/drawer` ya monta, escrita dos veces — y por eso se
            quedaba fuera de cada arreglo del cromo compartido. Ahora usa la carcasa
            común y solo conserva lo que de verdad es suyo: el filete azul del
            borde, su sombra y el ancho de la ventana de escritorio. */}
        <DrawerContent
          /* A pantalla completa en el teléfono: una tarea trae ubicación,
             tratamiento, productos, notas y el resumen del final, y con el tope
             de siempre el resumen caía justo por debajo del pliegue. */
          fit="expanded"
          style={{ touchAction: "pan-y" }}
          className={cn(
            "overflow-hidden focus:outline-none border-[#E4EDF5]",
            "shadow-[0_-10px_40px_-8px_rgba(15,39,68,0.22)]",
            "md:w-[calc(100vw-3rem)] md:max-w-[480px] lg:max-w-[560px] md:max-h-[88vh]",
            "md:border-slate-200/70",
          )}
        >
          {/* Header */}
          <div className="shrink-0 px-[18px] pt-1.5 md:pt-5">
            <div className="flex items-start gap-3">
              <div
                className="shrink-0 w-[46px] h-[46px] rounded-2xl flex items-center justify-center"
                style={{ backgroundColor: typeStyle.color, boxShadow: taskTypeTileShadow(typeStyle.color) }}
              >
                <TypeIcon className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0 pt-0.5">
                <DrawerTitle className="text-lg md:text-xl font-bold text-slate-900 leading-snug">
                  {translateTaskTitle(task.title, t)}
                </DrawerTitle>
                {/* LAS ETIQUETAS, BAJO EL TÍTULO Y EN SU COLUMNA (usuario
                    05-09-2026). Colgaban del bloque entero, así que empezaban en
                    el borde izquierdo, por debajo de la baldosa; ahora van dentro
                    de la columna del título y arrancan a la derecha del icono,
                    alineadas con él. Igual en todos los tamaños. */}
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <span className={PILL_BASE} style={{ backgroundColor: PILL_COLORS[STATUS_PILL_COLOR[task.status] ?? "slate"] }}>
                    {t(`planner.status.${task.status}`)}
                  </span>
                  <span className={PILL_BASE} style={{ backgroundColor: PILL_COLORS[TYPE_PILL_COLOR[task.type] ?? "slate"] }}>
                    {t(`planner.types.${task.type}`)}
                  </span>
                  {task.priority === "high" && (
                    <span className={PILL_BASE} style={{ backgroundColor: PILL_COLORS.red }}>
                      <Flag className="w-3 h-3" />
                      {t(`planner.priority.${task.priority}`)}
                    </span>
                  )}
                  {task.scouting_session_id && (
                    <button
                      type="button"
                      onClick={openLinkedScouting}
                      disabled={scoutingLoading}
                      aria-busy={scoutingLoading}
                      className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2 disabled:opacity-60"
                    >
                      <Badge className="rounded-full px-2.5 py-1 text-[11px] font-semibold h-auto bg-slate-100 text-slate-600 border border-slate-200 inline-flex items-center gap-1 hover:bg-slate-200 cursor-pointer transition-colors">
                        <Link2 className="w-3 h-3" />
                        {t("planner.linkedToScouting")}
                      </Badge>
                    </button>
                  )}
                </div>
              </div>
              <DrawerClose aria-label={t("common.close", "Close")} className={SHEET_CLOSE_CLASS}>
                <SheetCloseIcon />
              </DrawerClose>
            </div>

          </div>

          {/* Hairline divider between header and body */}
          <div className="mt-4 mx-[18px] h-px bg-slate-200/70 shrink-0" />

          <div
            data-sheet-scroll
            className="flex-1 min-h-0 overflow-y-auto overscroll-contain"
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            {/* El cuerpo no se pinta hasta que la hoja se ha posado. La tarea llega
                entera por props, pero los nombres y las áreas de los sectores y los
                componentes del tratamiento los traen dos efectos aparte, y su
                re-render caía dentro de la subida: la ficha se rehacía —secciones,
                desgloses por sector, fotos— mientras la hoja aún se movía, en el
                mismo hilo que la animaba, y eso es lo que se veía a tirones. No se
                retrasa ninguna petición, solo el instante de pintarla, así que
                consulta y animación se solapan en vez de sumarse. La cabecera (icono,
                título, fecha, la X y las etiquetas) y la barra de acciones de abajo se
                pintan desde el primer fotograma: la hoja tiene que decir qué es y qué
                se puede hacer nada más abrirse. */}
            {!settled ? (
              <SheetBodySkeleton card rows={4} className="pt-4" />
            ) : (
            <div className="px-[18px] py-4 space-y-5">

              {/* LA FICHA, EN UNA SOLA TABLA (usuario 05-09-2026). Antes esto eran
                  dos secciones con dos estilos: una tira de datos en línea para el
                  sitio y una caja aparte para los productos. Ahora es la misma tabla
                  de filas que la ficha de un parte de campo —baldosa, rótulo y dato—
                  en el orden que pidió el usuario: fecha, sitio, quién lo hace, la
                  plaga si la hay, y los productos al final. */}
              <div className="rounded-2xl bg-white border border-[#b6dded] shadow-sm divide-y divide-[#e7f1f7]">

                <FilaFicha tile={TILE_BLUE} icon={Calendar} label={t("common.date")}>
                  {formatDateTime(new Date(task.scheduled_date))}
                </FilaFicha>

                <FilaFicha tile={TILE_GREEN} icon={House} label={t("planner.location")}>
                  {/* El sitio y su superficie, en la misma línea (usuario
                      05-09-2026): la superficie es un dato DEL sitio, y colgando
                      sola dos renglones más abajo parecía otra cosa. Si no cabe,
                      baja sola: `flex-wrap`. */}
                  <span className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                    <span className="break-words">
                      {[locationName, task.greenhouses?.name].filter(Boolean).join(" · ") || "—"}
                    </span>
                    {(() => {
                      const allBlockIds = Array.from(new Set([
                        ...getTargetBlocks(task),
                        ...((task.custom_products ?? []).flatMap(p => p.target_blocks ?? [])),
                        ...(task.block_id ? [task.block_id] : []),
                      ]));
                      if (allBlockIds.length === 0 && !task.block_id) return null;
                      const totalAreaM2 = allBlockIds.reduce((s, id) => s + (blockAreaMap.get(id) ?? 0), 0);
                      if (totalAreaM2 <= 0) return null;
                      const areaLabel = totalAreaM2 >= 10000
                        ? `${(totalAreaM2 / 10000).toFixed(2)} ha`
                        : `${totalAreaM2.toLocaleString()} m²`;
                      return (
                        <span className="text-[13px] font-semibold tabular-nums text-slate-500">
                          {areaLabel}
                        </span>
                      );
                    })()}
                  </span>

                  {/* Los sectores y la superficie cuelgan del sitio: son el detalle
                      de DÓNDE, no un dato aparte que merezca su propia fila. */}
                  {(() => {
                    const targetBlocks = getTargetBlocks(task);
                    const observedRows = task.custom_products?.[0]?.observed_rows as Record<string, { row_start: number | null; row_end: number | null }> | undefined;
                    const names = targetBlocks.length > 0
                      ? targetBlocks
                      : (task.block_id ? [task.block_id] : []);
                    if (names.length === 0) return null;
                    return (
                      <span className="mt-1.5 flex flex-wrap items-center gap-1">
                        {names.map((blockId: string, idx: number) => {
                          const blockName = blockNameMap.get(blockId) || targetBlockNames[idx] || task.blocks?.name || t("planner.unknownBlock");
                          const rowData = observedRows?.[blockId];
                          const hasRow = rowData && (rowData.row_start != null || rowData.row_end != null);
                          return (
                            <Badge key={blockId} variant="secondary" className="text-[10px] font-medium px-1.5 py-0 h-5 bg-emerald-600 text-white border-transparent">
                              {blockName}
                              {hasRow && (
                                <span className="ml-1 inline-flex items-center gap-0.5 text-white/80">
                                  <Rows className="w-2.5 h-2.5" />
                                  {rowData.row_start != null && rowData.row_end != null
                                    ? t("planner.rowRange", { start: rowData.row_start, end: rowData.row_end })
                                    : `${rowData.row_start ?? rowData.row_end}`}
                                </span>
                              )}
                            </Badge>
                          );
                        })}
                      </span>
                    );
                  })()}
                </FilaFicha>

                {/* Quién lo hace. La baldosa es su foto, como en la ficha del parte. */}
                <FilaFicha
                  label={t("planner.assignedTo")}
                  avatar={
                    task.profiles?.full_name
                      ? <PersonAvatar name={task.profiles.full_name} avatarUrl={task.profiles.avatar_url} />
                      : undefined
                  }
                  tile={TILE_SLATE}
                  icon={User}
                >
                  {task.profiles?.full_name || (
                    <span className="font-normal text-slate-400">{t("planner.unassigned")}</span>
                  )}
                </FilaFicha>

                {/* La plaga va ENCIMA de los productos: primero contra qué, luego con
                    qué (usuario 05-09-2026). */}
                {task.pests && (
                  <FilaFicha tile={TILE_AMBER} icon={Bug} label={t("planner.pest")}>
                    <span className="flex flex-wrap items-center gap-2">
                      {getPestName(task.pests)}
                      {(isBio || isChem) && (
                        <span className={PILL_BASE} style={{ backgroundColor: PILL_COLORS[isBio ? "green" : "red"] }}>
                          {isBio ? t("sprayLogs.filters.biological") : t("sprayLogs.filters.chemical")}
                        </span>
                      )}
                    </span>
                  </FilaFicha>
                )}

                {task.type === "treatment" && (task.treatments || hasProducts || task.amount_planned) && (
                  <FilaFicha tile={TILE_VIOLET} icon={FlaskConical} label={t("planner.products")}>
                    <div className="divide-y divide-slate-100 -mt-0.5">
                      {hasProducts ? (
                        task.custom_products!.map((p, idx) => {
                          const tBlocks = p.target_blocks || task.custom_products?.[0]?.target_blocks;
                          const showBreakdown = Array.isArray(tBlocks) && tBlocks.length > 1;
                          const totalArea = showBreakdown ? tBlocks!.reduce((sum: number, id: string) => sum + (blockAreaMap.get(id) ?? 0), 0) : 0;
                          const hasArea = totalArea > 0;
                          const expanded = expandedProducts.has(idx);
                          // Applied dose: prefer the entered rate (covers /ha, /100L and absolute),
                          // falling back to the legacy per-caldo dose fields.
                          const doseValue = p.rate_value ?? p.dose_value;
                          const doseUnit = p.rate_unit ?? p.dose_unit;
                          return (
                            <div key={idx} className="py-2 first:pt-0 space-y-1">
                              <div
                                role={showBreakdown ? "button" : undefined}
                                onClick={showBreakdown ? () => toggleProduct(idx) : undefined}
                                className={cn("w-full flex items-center justify-between gap-3 text-left", showBreakdown && "cursor-pointer")}
                              >
                                <span className="flex items-center gap-2 min-w-0">
                                  <span className={cn("w-2 h-2 rounded-full shrink-0", a.dot)} />
                                  <span className="font-semibold text-[14px] text-slate-900 truncate">{p.product_name}</span>
                                </span>
                                <span className="flex items-center gap-2 shrink-0">
                                  <span className="font-bold text-[14px] text-slate-900 whitespace-nowrap tabular-nums text-right">
                                    {Number.isFinite(Number(p.amount)) ? new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 4 }).format(Number(p.amount)) : p.amount} {p.unit}
                                  </span>
                                  {showBreakdown && (
                                    <ChevronDown className={cn("w-4 h-4 text-slate-400 transition-transform", expanded && "rotate-180")} />
                                  )}
                                </span>
                              </div>
                              {(doseValue != null && doseUnit) && (
                                <div className="pl-4 text-[12px] font-normal text-slate-900 flex items-center gap-1">
                                  <FlaskConical className="w-3 h-3" />
                                  <span>{t("sprayLogs.appliedDose")}: {doseValue} {doseUnit}</span>
                                </div>
                              )}
                              {showBreakdown && expanded && (
                                <div className="pl-4 mt-1 space-y-1">
                                  <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">
                                    {t("planner.perBlockBreakdown")}
                                    {!hasArea && <span className="ml-1 italic normal-case">({t("planner.equalDistribution")})</span>}
                                  </p>
                                  {tBlocks!.map((blockId: string) => {
                                    const name = blockNameMap.get(blockId) || t("planner.unknownBlock");
                                    const rawBlockAmount = hasArea
                                      ? Math.round(((blockAreaMap.get(blockId) ?? 0) / totalArea) * p.amount * 100) / 100
                                      : Math.round((p.amount / tBlocks!.length) * 100) / 100;
                                    // La fila de arriba ya se guardaba de NaN; el reparto
                                    // por sector no, y sale del mismo p.amount.
                                    const blockAmount = Number.isFinite(rawBlockAmount) ? rawBlockAmount : "—";
                                    return (
                                      <div key={blockId} className="flex items-center justify-between text-[13px] font-normal">
                                        <span className="flex items-center gap-1 text-slate-500">
                                          <MapPin className="w-3.5 h-3.5" />
                                          {name}
                                        </span>
                                        <span className="font-semibold text-slate-900 tabular-nums">{blockAmount} {p.unit}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })
                      ) : treatmentComponents.length > 0 ? (
                        // Los componentes de la receta. Enseñamos la DOSIS tal cual
                        // está guardada (p. ej. 1,5 L/ha), no una cantidad absoluta:
                        // esta tarea no registró litros y no vamos a inventarlos.
                        treatmentComponents.map((c) => (
                          <div key={c.id} className="flex items-center justify-between gap-3 py-2 first:pt-0">
                            <span className="flex items-center gap-2 min-w-0">
                              <span className={cn("w-2 h-2 rounded-full shrink-0", a.dot)} />
                              <span className="font-semibold text-[14px] text-slate-900 truncate">
                                {c.product_name}
                              </span>
                            </span>
                            {c.rate_value != null && c.rate_unit && (
                              <span className="font-bold text-[14px] text-slate-900 whitespace-nowrap tabular-nums shrink-0">
                                {c.rate_value} {c.rate_unit}
                              </span>
                            )}
                          </div>
                        ))
                      ) : task.amount_planned ? (
                        <div className="flex items-center gap-1.5 text-[13px] font-normal text-slate-600">
                          <Package className="w-3.5 h-3.5 text-slate-400" />
                          <span>{t("planner.plannedAmount")}: <strong className="text-slate-900">{task.amount_planned}</strong></span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 text-[13px] font-normal text-slate-500">
                          <Package className="w-3.5 h-3.5 text-slate-400" />
                          <span>{t("planner.noProductsRecorded", "Sin productos registrados en esta tarea")}</span>
                        </div>
                      )}

                    </div>
                  </FilaFicha>
                )}

                {/* EL AGUA, UNA FILA ENTERA (usuario 05-09-2026). Iba metida al final
                    de la caja de productos, en azul y a 12 px, y se leía como una nota
                    al pie de los productos cuando es un dato del mismo rango: cuánto
                    caldo se prepara. Ahora es una fila más de la tabla, con su baldosa
                    de gotas, el rótulo a la izquierda y la cantidad a la derecha, igual
                    que una línea de producto. Sin el kicker en versalitas: el rótulo ES
                    el dato, no la etiqueta de otro. */}
                {task.custom_products?.[0]?.water_volume != null && (
                  <div className="flex items-center gap-3 p-3">
                    <div
                      className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                      style={{ backgroundColor: TILE_SKY, boxShadow: taskTypeTileShadow(TILE_SKY) }}
                    >
                      <Droplets className="w-4 h-4 text-white" />
                    </div>
                    <span className="min-w-0 flex-1 truncate font-semibold text-sm text-slate-900">
                      {t("sprayLogs.waterVolumeLabel")}
                    </span>
                    <span className="shrink-0 whitespace-nowrap text-right font-bold text-sm text-slate-900 tabular-nums">
                      {task.custom_products[0].water_volume} {task.custom_products[0].water_volume_unit || "L"}
                      {(task.custom_products[0] as any).water_volume_per_ha != null && (
                        <span className="ml-1 font-semibold text-slate-400">
                          ({(task.custom_products[0] as any).water_volume_per_ha} L/ha)
                        </span>
                      )}
                    </span>
                  </div>
                )}
              </div>

              {/* Time Tracking — only timing, not assignee (already shown above) */}
              {(task.actual_start_time || task.completed_at || task.time_spent) && (
                <section className="space-y-2">
                  <h3 className={sectionLabel}>{t("planner.timeTracking")}</h3>
                  <div className={groupedCard}>
                    {task.actual_start_time && (
                      <div className="flex items-center justify-between text-[13px] py-3">
                        <span className="text-slate-500 flex items-center gap-1.5">
                          <Clock className="w-3.5 h-3.5" />
                          {t("planner.startedAt")}
                        </span>
                        <span className="font-semibold text-slate-900">
                          {formatDate(new Date(task.actual_start_time), "MMM d, h:mm a")}
                        </span>
                      </div>
                    )}
                    {task.completed_at && (
                      <div className="flex items-center justify-between text-[13px] py-3">
                        <span className="text-slate-500 flex items-center gap-1.5">
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          {t("planner.completedAt")}
                        </span>
                        <span className="font-semibold text-slate-900">
                          {formatDate(new Date(task.completed_at), "MMM d, h:mm a")}
                        </span>
                      </div>
                    )}
                    {task.time_spent && (
                      <div className="flex items-center justify-between text-[13px] py-3">
                        <span className="text-slate-500 flex items-center gap-1.5">
                          <Timer className="w-3.5 h-3.5" />
                          {t("planner.timeSpent")}
                        </span>
                        <span className="font-semibold text-slate-900">
                          {Math.floor(task.time_spent / 60)}h {task.time_spent % 60}m
                        </span>
                      </div>
                    )}
                  </div>
                </section>
              )}

              {/* Visit/Inspection Report */}
              {isVisitOrInspection && (
                <section className="space-y-2">
                  <div className="flex items-center justify-between px-1">
                    <h3 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                      {t("perito.seguimiento.visitReport")}
                    </h3>
                    {canWriteReport && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditReportOpen(true)}
                        className="h-7 text-[11px] px-2"
                      >
                        <Pencil className="w-3 h-3 mr-1" />
                        {t("perito.seguimiento.editReport")}
                      </Button>
                    )}
                    {canCommentReport && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCommentOpen(true)}
                        className="h-7 text-[11px] px-2"
                      >
                        <MessageSquareText className="w-3 h-3 mr-1" />
                        {visitRow?.visit_report?.company_comment?.text
                          ? t("perito.seguimiento.editComment")
                          : t("perito.seguimiento.addComment")}
                      </Button>
                    )}
                  </div>
                  {visitReport && (visitReport.observations || visitReport.proposals || visitReport.incidents || visitReport.checklist?.length > 0) ? (
                    <div className="bg-white rounded-[14px] border border-slate-200/80 p-3.5 space-y-2 shadow-[0_1px_2px_rgba(15,39,68,0.04)]">
                      {visitReport.observations && (
                        <div>
                          <span className="text-[10px] font-medium text-slate-400 uppercase">{t("perito.seguimiento.observations")}</span>
                          <p className="text-[13px] text-slate-700 whitespace-pre-wrap mt-0.5">{visitReport.observations}</p>
                        </div>
                      )}
                      {visitReport.checklist?.length > 0 && (
                        <div>
                          <span className="text-[10px] font-medium text-slate-400 uppercase">{t("perito.seguimiento.checklist")}</span>
                          <div className="mt-0.5 space-y-0.5">
                            {visitReport.checklist.map((item: any, i: number) => (
                              <div key={i} className="flex items-center gap-1.5 text-[13px]">
                                {item.status === "ok" ? <CheckCircle2 className="w-3 h-3 text-green-600" />
                                  : item.status === "ko" ? <XCircle className="w-3 h-3 text-red-600" />
                                  : <Clock className="w-3 h-3 text-amber-600" />}
                                <span>{item.item}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {visitReport.proposals && (
                        <div>
                          <span className="text-[10px] font-medium text-slate-400 uppercase">{t("perito.seguimiento.proposals")}</span>
                          <p className="text-[13px] text-slate-700 whitespace-pre-wrap mt-0.5">{visitReport.proposals}</p>
                        </div>
                      )}
                      {visitReport.incidents && (
                        <div>
                          <span className="text-[10px] font-medium text-slate-400 uppercase">{t("perito.seguimiento.incidents")}</span>
                          <p className="text-[13px] text-slate-700 whitespace-pre-wrap mt-0.5">{visitReport.incidents}</p>
                        </div>
                      )}
                    </div>
                  ) : visitQuery.isLoading ? (
                    <div className="bg-white rounded-[14px] border border-slate-200/80 p-3.5 text-center shadow-[0_1px_2px_rgba(15,39,68,0.04)]">
                      <p className="text-[13px] text-slate-400">{t("perito.seguimiento.loadingReport")}</p>
                    </div>
                  ) : visitQuery.isError ? (
                    <div className="bg-white rounded-[14px] border border-destructive/30 p-3.5 text-center">
                      <p className="text-[13px] text-destructive">{t("perito.seguimiento.reportLoadError")}</p>
                    </div>
                  ) : (
                    <div className="bg-white rounded-[14px] border border-slate-200/80 p-3.5 text-center shadow-[0_1px_2px_rgba(15,39,68,0.04)]">
                      <p className="text-[13px] text-slate-400">{t("perito.seguimiento.noReport")}</p>
                    </div>
                  )}
                  {visitRow && (
                    <VisitReportMeta
                      report={visitRow.visit_report}
                      completedAt={visitRow.completed_at}
                      status={visitRow.status}
                      names={visitRow.names}
                      assigneeId={visitRow.assigned_to}
                      compact
                    />
                  )}
                </section>
              )}

              {/* Photos */}
              {reportPhotos.length > 0 && (
                <section className="space-y-2">
                  <h3 className={sectionLabel}>{t("perito.seguimiento.attachments")}</h3>
                  <div className="grid grid-cols-3 gap-1.5">
                    {reportPhotos.map((url: string, i: number) => (
                      <a key={i} href={url} target="_blank" rel="noopener noreferrer">
                        <ImgOffline
                          src={url}
                          alt={`Photo ${i + 1}`}
                          className="w-full h-20 object-cover rounded-md border hover:opacity-80 transition-opacity cursor-pointer"
                        />
                      </a>
                    ))}
                  </div>
                </section>
              )}

              {/* AQUÍ IBA «RESUMEN» y se ha quitado (usuario 05-09-2026). Era una
                  línea derivada —«Aceite de neem 0,8 L/ha · Volumen de agua: 1000 L
                  · 5 sectores»— que tenía sentido cuando esta ficha era un revoltijo
                  y hacía falta un recuento de una línea. Desde que la ficha es una
                  tabla, las tres cosas que decía ya están arriba: los productos con
                  su dosis, el agua en su propia fila y los sectores en chapas bajo
                  la ubicación. Repetirlas debajo no añadía nada.
                  Se fue con él `src/lib/taskSummary.ts`, que no lo usaba nadie más. */}

              {/* Notes — manual, only what the user actually wrote. */}
              {task.notes && (
                <section className="space-y-2">
                  <h3 className={sectionLabel}>{t("planner.notes")}</h3>
                  <div className="bg-white rounded-[14px] border border-slate-200/80 p-3.5 shadow-[0_1px_2px_rgba(15,39,68,0.04)]">
                    <p className="text-[13px] text-slate-700 whitespace-pre-wrap">{task.notes}</p>
                  </div>
                </section>
              )}
            </div>
            )}
          </div>

          {hasActionBar && (
            <div className="shrink-0 border-t border-slate-200/70 bg-slate-50/70 px-[18px] pt-3 pb-[max(14px,var(--safe-bottom))] space-y-2.5">
              {/* Primary action — Completar tarea («Marcar pendiente» va abajo, secundario) */}
              {showComplete ? (
                <Button
                  onClick={handleCompleteClick}
                  disabled={isLoading}
                  className="w-full h-12 rounded-2xl text-[15px] font-semibold text-white border-0 bg-gradient-to-r from-[#5cc0c9] to-[#4f7fd6] hover:opacity-95 shadow-[0_8px_20px_-6px_rgba(79,127,214,0.55)]"
                >
                  <CheckCircle2 className="w-5 h-5 mr-2" />
                  {t("planner.completeTask", "Completar tarea")}
                </Button>
              ) : null}

              {/* Secondary controls — Comenzar / Editar + compact red trash */}
              {hasSecondaryRow && (
                <div className="flex items-center gap-2.5">
                  {markPendingBtn}
                  {startStopBtn}
                  {editBtn}
                  {deleteBtn}
                </div>
              )}
            </div>
          )}

        </DrawerContent>

      {/* «Marcar pendiente»: qué se deshace, antes de deshacerlo (A17). */}
      <AlertDialog open={undoDialogOpen} onOpenChange={setUndoDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("planner.undoConfirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{undoText}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleUndoConfirm}>
              {t("planner.markPending", "Marcar pendiente")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("planner.deleteTask")}</AlertDialogTitle>
            <AlertDialogDescription>
              {linkedSprayLogs.length > 0
                ? t("planner.deleteTaskWithSprayLogWarning", { date: formatDate(new Date((linkedSprayLogs[0].date || "") + "T00:00:00"), "PPP") })
                : t("planner.deleteTaskConfirmation")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? t("common.deleting") : t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Muestreo vinculado — se pinta encima de esta ficha, como el resto de ventanas
          anidadas de aquí. Al cerrarlo, la tarea sigue abierta debajo: no se toca `open`. */}
      <ScoutingSessionDetailDrawer
        open={scoutingOpen}
        onOpenChange={setScoutingOpen}
        session={scoutingSession}
        onSessionUpdated={() => onRefresh?.()}
        onSessionDeleted={() => {
          setScoutingOpen(false);
          setScoutingSession(null);
          onRefresh?.();
        }}
      />

      {/* Edit Visit Report Dialog */}
      {isVisitOrInspection && editReportOpen && (
        <VisitCompletionDialog
          open={editReportOpen}
          onOpenChange={setEditReportOpen}
          task={task}
          editOnly
          onSuccess={() => {
            setEditReportOpen(false);
            onRefresh?.();
          }}
        />
      )}

      {/* Comentario de la empresa (admin / encargado): aparte del informe del perito */}
      {isVisitOrInspection && commentOpen && task && (
        <VisitCommentDialog
          open={commentOpen}
          onOpenChange={setCommentOpen}
          taskId={task.id}
          initialText={visitRow?.visit_report?.company_comment?.text ?? ""}
          onSaved={() => onRefresh?.()}
        />
      )}
    </Drawer>
  );
}
